
import { Box, Dialog, DialogContent, TextField, Typography } from "@mui/material"
import { BoxFC, BoxFR } from "components/BoxCustom"
import ComboBox from "components/ComboBox"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import PaperComponent from "components/PaperComponent"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import { DesktopDatePicker } from "@mui/x-date-pickers-pro"
import dayjs from "dayjs"
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { addIdForDataGrid } from "utils"
import { tyreColNm } from "./constant"
import { alertError } from "components/Alert"
import { bahtProps } from "utils/bahtProps"

const columns = [
  { field: 'SupSNm', headerName: tyreColNm.SupId, width: 150 },
  { field: 'TyrCd', headerName: tyreColNm.TyrCd, width: 125 },
  { field: 'TyrBrand', headerName: tyreColNm.TyrBrand, width: 110 },
  { field: 'TyrModel', headerName: tyreColNm.TyrModel, width: 110 },
  { field: 'TyrSzNm', headerName: tyreColNm.TyrSzId, width: 120 },
  { field: 'Dscp', headerName: tyreColNm.Dscp, flex: 1, minWidth: 100 },
]

const ReceiveRetreadDialog = ({ dialogOpen, setDialogOpen, onFinish }) => {
  const { ax, msData } = useContext(GlobalStateContext)

  const [trnsDte, setTrnsDte] = useState(dayjs().format("YYYY-MM-DD"))
  const [supId, setSupId] = useState(null)
  const [untPr, setUntPr] = useState("")
  const [docNo, setDocNo] = useState("")
  const [docDte, setDocDte] = useState(null)
  const [retreadTyres, setRetreadTyres] = useState([])
  const [filterTyrCd, setFilterTyrCd] = useState("")
  const [selectedIds, setSelectedIds] = useState([])

  const loadRetreadTyres = useCallback(() => {
    ax.post("/tyre/getTyre", { TyrStsId: 4 }).then(value => {
      if (value.data) setRetreadTyres(addIdForDataGrid(value.data, 'TyrId'))
    })
  }, [ax])

  useEffect(() => {
    if (dialogOpen) {
      loadRetreadTyres()
    } else {
      setTrnsDte(dayjs().format("YYYY-MM-DD"))
      setSupId(null)
      setUntPr("")
      setDocNo("")
      setDocDte(null)
      setRetreadTyres([])
      setFilterTyrCd("")
      setSelectedIds([])
    }
  }, [dialogOpen, loadRetreadTyres])

  const filteredTyres = useMemo(() => {
    const keyword = filterTyrCd.trim().toLowerCase()
    return retreadTyres.filter(t =>
      t.SupId === supId
      && (!keyword || (t.TyrCd || "").toLowerCase().includes(keyword))
    )
  }, [retreadTyres, supId, filterTyrCd])

  const selectedCount = useMemo(() => selectedIds.length, [selectedIds])

  const handleDialogOk = useCallback(async () => {
    if (selectedIds.length === 0) {
      alertError("กรุณาเลือกยางที่รับคืน")
      return
    }
    
    const tyrItems = selectedIds.map(tyrId => ({
      TyrId: tyrId,
      UntPr: untPr ? Number(untPr) : null,
    }))
    const value = await ax.post("/tyre/receiveRetread", {
      TyrItems: tyrItems,
      TrnsDte: trnsDte,
      SupId: supId,
      DocNo: docNo || null,
      DocDte: docDte || null,
    })
    if (value.data) {
      onFinish()
      setDialogOpen(false)
    }
  }, [ax, selectedIds, supId, untPr, trnsDte, docNo, docDte, onFinish, setDialogOpen])

  return (
    <Dialog
      open={dialogOpen}
      onClose={(e, reason) => { if (reason === "backdropClick") return; setDialogOpen(false) }}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth='md'
    >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)} title="รับยางหล่อดอก" />
      <DialogContent>
        <BoxFC pt={2} sx={{ gap: 1, height: 540 }}>
          <BoxFR sx={{ gap: 1 }}>
            <DesktopDatePicker
              label="วันที่รับ"
              inputFormat="DD/MM/YYYY"
              value={trnsDte}
              onChange={(v) => setTrnsDte(v ? dayjs(v).format("YYYY-MM-DD") : null)}
              renderInput={(params) => <TextField size="small" sx={{ width: 160 }} {...params} />}
            />
            <ComboBox
              options={msData.supplierCombo || []}
              label="ผู้จำหน่าย"
              selectedId={supId}
              setSelectedId={setSupId}
              sx={{ width: 150 }}
            />
            <TextField
              size="small" label="ราคา/เส้น" type="number"
              {...bahtProps}
              required
              error={!untPr}
              value={untPr}
              onChange={(e) => setUntPr(e.target.value)}
              sx={{ width: 160 }}
            />
            <TextField
              size="small" label="เลขที่เอกสาร"
              value={docNo}
              onChange={(e) => setDocNo(e.target.value)}
              sx={{ width: 150 }}
            />
            <DesktopDatePicker
              label="วันที่เอกสาร"
              inputFormat="DD/MM/YYYY"
              value={docDte}
              onChange={(v) => setDocDte(v ? dayjs(v).format("YYYY-MM-DD") : null)}
              renderInput={(params) => <TextField size="small" sx={{ width: 160 }} {...params} />}
            />
          </BoxFR>
          <BoxFR sx={{ gap: 1 }}>
            <TextField
              size="small" label="Filter รหัสยาง"
              value={filterTyrCd}
              onChange={(e) => setFilterTyrCd(e.target.value)}
              sx={{ width: 160 }}
            />
            <Box flex={1} />
            <Typography variant="subtitle2">
              ยางที่ส่งหล่อดอก ({filteredTyres.length} รายการ)
            </Typography>
            {selectedCount > 0 && (
              <Typography variant="subtitle2" color="primary">
                เลือกแล้ว {selectedCount} รายการ
              </Typography>
            )}
          </BoxFR>
          <Box flex={1} overflow='hidden'>
            <DataGridCellExpand
              hideFooter
              checkboxSelection
              rows={filteredTyres}
              columns={columns}
              selectionModel={selectedIds}
              onSelectionModelChange={(ids) => setSelectedIds(ids)}
            />
          </Box>
        </BoxFC>
      </DialogContent>
      <DialogFooter okText="รับยาง"
        handleDialogClose={() => setDialogOpen(false)}
        handleDialogOk={handleDialogOk}
      />
    </Dialog>
  )
}

export default React.memo(ReceiveRetreadDialog)
